import type { AppMenuItemProps, AppMenuItemState } from ".";
import { useMenuItem_unstable } from "@fluentui/react-components";
import type { RefObjectFunction } from "@fluentui/react-hooks";
import { useAppComponentKeytip, getDefaultKeytipProps } from "@1js/acui-keytip";
import type { IKeytipProps } from "@fluentui/react/lib/Keytip";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";
import type { AppKeytipProps, DeriveKeytipFunction } from "@1js/acui-keytip";
import { useShortcutPillsEnabled } from "../../hooks/useNextComponentsEnabled";
import { renderShortcutPills } from "./ShortcutPills";

export const deriveMenuItemKeytip: DeriveKeytipFunction = memoizeFunction(
  (
    keytipProps?: AppKeytipProps,
    onExecute?: (executeTarget: HTMLElement, target: HTMLElement) => void
  ): AppKeytipProps => {
    return keytipProps
      ? getDefaultKeytipProps(
          keytipProps,
          (keytipProps as IKeytipProps).offset?.x || 100,
          onExecute
        )
      : undefined;
  }
);

export const useAppMenuItem = (
  props: AppMenuItemProps,
  ref: React.Ref<HTMLDivElement>
): AppMenuItemState => {
  const { keytipProps } = props;
  const keytipPropsFinal: AppKeytipProps = keytipProps
    ? { ...keytipProps, disabled: props.disabled }
    : undefined;
  let mergedRefWithKeytip: RefObjectFunction<HTMLElement | null | undefined>;
  mergedRefWithKeytip = useAppComponentKeytip(
    props,
    ref as RefObjectFunction<HTMLElement>,
    "AppButton",
    () =>
      deriveMenuItemKeytip(
        keytipPropsFinal,
        (executeTarget: HTMLElement, target: HTMLElement) => {
          keytipPropsFinal?.onExecute?.(executeTarget, target);
        }
      ),
    {
      keytipProps: keytipPropsFinal,
    }
  );

  const refFinal = mergedRefWithKeytip;

  const shortcutPillsEnabled = useShortcutPillsEnabled();

  const propsWithSecondaryContent = {
    ...props,
    // Set aria-checked if role is menuitemradio.
    "aria-checked": props.role === "menuitemradio" ? props.checked : undefined,
    secondaryContent: shortcutPillsEnabled
      ? renderShortcutPills({
          shortcutMetadata: props.shortcutMetadata,
          secondaryText: props.secondaryText,
          showAllShortcuts: false,
        })
      : props.secondaryContent,
  };
  const state = useMenuItem_unstable(
    propsWithSecondaryContent,
    refFinal
  ) as AppMenuItemState;
  state.footerItem = props.footerItem;
  return state;
};
